import { useEffect, useRef, useState } from 'react';
import { FormField, FormSelect, Modal, TextInput } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { adminApi, type IvrPromptRow } from '@/shared/api/adminApi';
import { apiClient } from '@/shared/api/client';
import { toast } from '@/components/ui/Toast';

/**
 * בחירת הקלטה לצומת IVR ("הקש 1 למכירות...") + ניהול ספריית ההקלטות:
 * העלאת קובץ, יצירה מטקסט (TTS) והשמעה מקדימה. ה-preview מוגש מאחורי
 * הטוקן — נמשך כ-blob (כמו צליל ההמתנה).
 */

async function fetchPreviewUrl(promptId: number): Promise<string> {
  const res = await apiClient.get(`/admin/ivr-prompts/${promptId}/preview`, { responseType: 'blob' });
  return URL.createObjectURL(res.data as Blob);
}

export function PromptPreviewButton({ promptId }: { promptId: number }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  if (url) return <audio controls src={url} style={{ height: 30, maxWidth: 220 }} />;

  return (
    <button
      type="button"
      className="btn ghost"
      style={{ padding: '2px 8px', fontSize: 'var(--t-xs)' }}
      disabled={loading}
      onClick={async () => {
        setLoading(true);
        try {
          setUrl(await fetchPreviewUrl(promptId));
        } catch {
          toast('שגיאה בטעינת ההקלטה', 'danger');
        } finally {
          setLoading(false);
        }
      }}
    >
      <Icon name="play" size={12} /> השמע
    </button>
  );
}

export function PromptField({
  label, value, prompts, onChange, onManage,
}: {
  label: string;
  value?: number | null;
  prompts: IvrPromptRow[];
  onChange: (id: number | null) => void;
  onManage: () => void;
}) {
  return (
    <FormField label={label} hint={prompts.length === 0 ? 'אין הקלטות עדיין — לחץ "נהל הקלטות" כדי להעלות או ליצור מטקסט.' : undefined}>
      <div className="stack" style={{ gap: 6 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <FormSelect value={value ? String(value) : ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)} style={{ flex: 1 }}>
            <option value="">— ללא הקלטה —</option>
            {prompts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </FormSelect>
          <button type="button" className="btn" onClick={onManage} title="ניהול ספריית ההקלטות">
            <Icon name="mic" size={14} />
          </button>
        </div>
        {value ? <PromptPreviewButton promptId={value} /> : null}
      </div>
    </FormField>
  );
}

/** מודל ניהול ספריית ההקלטות של המרכזיה. */
export function PromptsManager({ prompts, onClose, onChanged }: {
  prompts: IvrPromptRow[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [tab, setTab] = useState<'upload' | 'tts'>('tts');
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // קטלוג ElevenLabs (מודלים + קולות) + הבחירה הנוכחית.
  const [catalog, setCatalog] = useState<{ models: { id: string; name: string; hebrew: boolean }[]; voices: { id: string; name: string }[]; hebrew_model: string } | null>(null);
  const [model, setModel] = useState('');
  const [voice, setVoice] = useState('');
  useEffect(() => {
    void adminApi.ttsCatalog().then((c) => {
      setCatalog(c);
      setModel(c.hebrew_model || c.models[0]?.id || '');
      setVoice(c.voices[0]?.id || '');
    }).catch(() => {});
  }, []);

  async function createTts() {
    if (!name.trim() || !text.trim()) { toast('יש למלא שם וטקסט', 'danger'); return; }
    setBusy(true);
    try {
      await adminApi.createIvrPromptTts(name.trim(), text.trim(), { model_id: model || undefined, voice_id: voice || undefined });
      toast('ההקלטה נוצרה', 'ok');
      setName(''); setText('');
      onChanged();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה ביצירת ההקלטה', 'danger');
    } finally {
      setBusy(false);
    }
  }

  async function upload(file: File) {
    if (!name.trim()) { toast('יש למלא שם להקלטה לפני ההעלאה', 'danger'); return; }
    if (file.size > 5 * 1024 * 1024) { toast('קובץ עד 5MB', 'danger'); return; }
    setBusy(true);
    try {
      await adminApi.uploadIvrPrompt(name.trim(), file);
      toast('הועלה', 'ok');
      setName('');
      onChanged();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בהעלאה', 'danger');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function remove(p: IvrPromptRow) {
    try {
      await adminApi.deleteIvrPrompt(p.id);
      toast('נמחק', 'ok');
      onChanged();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה במחיקה', 'danger');
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="ספריית הקלטות IVR"
      footer={<button type="button" className="btn" onClick={onClose}>סגור</button>}>
      <div className="stack" style={{ gap: 14 }}>
        {/* יצירה */}
        <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 12 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <button type="button" className={'btn' + (tab === 'tts' ? ' primary' : '')} onClick={() => setTab('tts')}>יצירה מטקסט</button>
            <button type="button" className={'btn' + (tab === 'upload' ? ' primary' : '')} onClick={() => setTab('upload')}>העלאת קובץ</button>
          </div>
          <div className="stack" style={{ gap: 8 }}>
            <FormField label="שם ההקלטה" required>
              <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder='למשל: "תפריט ראשי"' />
            </FormField>
            {tab === 'tts' ? (
              <>
                <FormField label="הטקסט שיוקרא" hint='למשל: "שלום וברוכים הבאים. למכירות הקישו 1, לשירות לקוחות הקישו 2."'>
                  <textarea
                    className="input"
                    rows={3}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    style={{ width: '100%', resize: 'vertical', font: 'inherit', padding: 8 }}
                  />
                </FormField>
                {catalog && (
                  <div style={{ display: 'grid', gridTemplateColumns: catalog.voices.length ? '1fr 1fr' : '1fr', gap: 8 }}>
                    <FormField label="מנוע קול (מודל)" hint={catalog.models.find((m) => m.id === model)?.hebrew ? 'תומך עברית ✓' : 'מומלץ מודל שתומך עברית'}>
                      <select className="input" value={model} onChange={(e) => setModel(e.target.value)} style={{ width: '100%', padding: 8 }}>
                        {catalog.models.map((m) => (
                          <option key={m.id} value={m.id}>{m.name}{m.hebrew ? ' — עברית' : ''}</option>
                        ))}
                      </select>
                    </FormField>
                    {catalog.voices.length > 0 && (
                      <FormField label="קול">
                        <select className="input" value={voice} onChange={(e) => setVoice(e.target.value)} style={{ width: '100%', padding: 8 }}>
                          {catalog.voices.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                      </FormField>
                    )}
                  </div>
                )}
                <div>
                  <button type="button" className="btn primary" onClick={createTts} disabled={busy}>
                    {busy ? 'יוצר…' : 'צור הקלטה'}
                  </button>
                </div>
              </>
            ) : (
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/ogg,.mp3,.wav,.ogg"
                  style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
                />
                <button type="button" className="btn primary" onClick={() => fileRef.current?.click()} disabled={busy}>
                  {busy ? 'מעלה…' : 'בחר קובץ אודיו (mp3/wav/ogg)'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* רשימה */}
        {prompts.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין הקלטות עדיין.</div>
        ) : (
          <div className="stack" style={{ gap: 6 }}>
            {prompts.map((p) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 'var(--t-sm)' }}>{p.name}</div>
                  <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.source === 'tts' ? `TTS: ${p.tts_text ?? ''}` : p.original_filename ?? 'קובץ'}
                  </div>
                </div>
                <PromptPreviewButton promptId={p.id} />
                <button type="button" className="btn danger" style={{ padding: '2px 8px' }} onClick={() => remove(p)}>
                  <Icon name="trash" size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
